import to from "await-to-js";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

import { AuthApiService } from "~/common/api/services/auth-api-service";
import {
  type MeResponse,
  UserRole,
} from "~/common/api/validation/auth-schemas";
import { dataStorage } from "~/common/services/data-storage-service";
import { promiseWithResolvers } from "~/common/utils/promise-with-resolvers";

export interface CurrentUser extends MeResponse {}

export interface CurrentUserStoreState {
  user: CurrentUser | null;

  isLoaded: boolean;

  isLoading: boolean;

  hasNoUser: boolean;

  userPromise: Promise<CurrentUser> | null;

  loadUser: () => Promise<CurrentUserStoreState | null>;

  logout: () => void | Promise<void>;

  isCompanyAdmin: () => boolean;
}

export const useCurrentUserStore = create<CurrentUserStoreState>((set, get) => {
  const {
    promise: userPromise,
    resolve: userPromiseResolver,
    reject: userPromiseReject,
  } = promiseWithResolvers();

  const authApiService = new AuthApiService();

  return {
    user: null,
    isLoaded: false,
    isLoading: false,
    hasNoUser: true,
    userPromise: userPromise as Promise<CurrentUser>,

    async loadUser() {
      if (!dataStorage.hasAuthTokens()) {
        set({ isLoaded: true, hasNoUser: true });

        return null;
      }

      set({ isLoading: true });

      const [error, user] = await to(authApiService.getMe());

      if (error) {
        set({ isLoading: false, isLoaded: true, hasNoUser: true });
        userPromiseReject();

        return null;
      }

      const newState = {
        ...get(),
        user,
        isLoaded: true,
        isLoading: false,
        hasNoUser: false,
      };

      set(newState);
      userPromiseResolver(user);

      return newState;
    },

    logout() {
      set({
        user: null,
        isLoaded: true,
        isLoading: false,
        hasNoUser: true,
      });
      dataStorage.clearAuthTokens();
    },

    isCompanyAdmin: () => {
      const { user } = get();

      if (!user) return false;

      return (
        user.role === UserRole.COMPANY_ADMIN || user.role === UserRole.OWNER
      );
    },
  };
});

export const useShallowCurrentUserStore = <T>(
  selector: (state: CurrentUserStoreState) => T,
) => useCurrentUserStore(useShallow(selector));
